import { WORKSPACE_ZOOM_MIN } from '@/components/canvas/shared/constants/legacyConfig';
import type { Page, PageWorkspaceLayout } from '@/components/canvas/document/publicTypes';
import type { WorkspacePageLayoutMode } from '@/components/canvas/workspace/model/workspacePageLayoutMode';

export const PAGE_WORKSPACE_GAP = 80;

/** 页面标题栏高度（屏幕像素；世界坐标槽位 = 高度 / zoom） */
export const PAGE_CHROME_HEIGHT = 40;

/** 标题栏最小屏幕宽度，避免左右内容换行 */
export const PAGE_CHROME_MIN_SCREEN_WIDTH = 280;

function clampLayoutZoom(zoom: number): number {
  return Math.max(WORKSPACE_ZOOM_MIN, zoom > 0 ? zoom : 1);
}

/** 标题栏在世界坐标中的占位高度（随 zoom 缩小而增大，保证页头完整可见） */
export function pageChromeWorldHeight(zoom: number): number {
  return PAGE_CHROME_HEIGHT / clampLayoutZoom(zoom);
}

/**
 * 纵向模式：相邻页板顶边之间的世界坐标步进。
 * zoom=1 时为 PAGE_WORKSPACE_GAP；缩小时同比例增加间距。
 */
export function verticalBoardStep(zoom: number): number {
  const z = clampLayoutZoom(zoom);
  return PAGE_WORKSPACE_GAP + PAGE_CHROME_HEIGHT * (1 / z - 1);
}

/** 纵向模式：按当前 zoom 自上而下重排全部页板顶边 y */
export function relayoutPagesForVerticalZoom(pages: Page[], zoom: number): Page[] {
  const step = verticalBoardStep(zoom);
  let boardY = 0;
  return pages.map((page, index) => {
    const layout: PageWorkspaceLayout = { x: 0, y: boardY, zIndex: index };
    boardY += page.size.height + step;
    return { ...page, workspaceLayout: layout };
  });
}

/** 紧挨指定页下方摆放（纵向模式；workspaceLayout.y 为页板顶边） */
export function workspaceLayoutBelowPage(
  page: Page,
  pages: Page[],
  zoom: number,
): PageWorkspaceLayout {
  let maxZ = 0;
  for (const p of pages) {
    maxZ = Math.max(maxZ, p.workspaceLayout?.zIndex ?? 0);
  }
  return {
    x: page.workspaceLayout.x,
    y: page.workspaceLayout.y + page.size.height + verticalBoardStep(zoom),
    zIndex: maxZ + 1,
  };
}

/** 单页叠放 zIndex：指定页最高，其余按文档顺序 0…n-2（见 docs/工作区/布局.md） */
function assignSingleStackZIndices(pageCount: number, topIndex: number): number[] {
  const z = new Array<number>(pageCount);
  let next = 0;
  for (let i = 0; i < pageCount; i++) {
    if (i === topIndex) continue;
    z[i] = next++;
  }
  z[topIndex] = pageCount - 1;
  return z;
}

/**
 * 单页模式：全部页叠在同一世界坐标。
 * 默认 zIndex 随文档顺序递增（后页盖住前页）。
 * 传入 `topPageId` 时该页 zIndex 最高（布局切换、载入时保证激活/第一页在最上层）。
 */
export function relayoutPagesForSingleStack(pages: Page[], topPageId?: string): Page[] {
  if (pages.length === 0) return pages;
  const anchor = pages[0]!.workspaceLayout;
  const x = Number.isFinite(anchor?.x) ? anchor!.x : 0;
  const y = Number.isFinite(anchor?.y) ? anchor!.y : 0;
  const topIndex = topPageId ? pages.findIndex((p) => p.id === topPageId) : -1;
  const zIndices =
    topIndex >= 0
      ? assignSingleStackZIndices(pages.length, topIndex)
      : pages.map((_, index) => index);
  return pages.map((page, index) => {
    const rotation = page.workspaceLayout?.rotation;
    return {
      ...page,
      workspaceLayout: {
        x,
        y,
        zIndex: zIndices[index]!,
        ...(rotation !== undefined ? { rotation } : {}),
      },
    };
  });
}

/** 单页模式：就地重排叠放坐标（插入/复制后） */
export function applySingleStackLayout(pages: Page[]): void {
  const next = relayoutPagesForSingleStack(pages);
  for (let i = 0; i < pages.length; i++) {
    pages[i]!.workspaceLayout = next[i]!.workspaceLayout;
    pages[i]!.updatedAt = Date.now();
  }
}

/** 单页模式：新页叠放锚点位置，zIndex 为插入下标 */
export function workspaceLayoutOnSingleStack(
  pages: Page[],
  insertIndex: number,
): PageWorkspaceLayout {
  const anchor = pages[0]?.workspaceLayout ?? { x: 0, y: 0, zIndex: 0 };
  return {
    x: anchor.x,
    y: anchor.y,
    zIndex: insertIndex,
  };
}

/** 按布局模式重排全部页面（切换模式时调用） */
export function relayoutPagesForMode(
  pages: Page[],
  mode: WorkspacePageLayoutMode,
  zoom = 1,
  /** 单页模式：置于最上层的页 id（当前激活页或第一页） */
  singleStackTopPageId?: string,
): Page[] {
  if (pages.length === 0) return pages;

  if (mode === 'single') {
    return relayoutPagesForSingleStack(pages, singleStackTopPageId);
  }

  if (mode === 'vertical') {
    return relayoutPagesForVerticalZoom(pages, zoom);
  }

  let cursorX = 0;
  return pages.map((page, index) => {
    const layout: PageWorkspaceLayout = { x: cursorX, y: 0, zIndex: index };
    cursorX += page.size.width + PAGE_WORKSPACE_GAP;
    return { ...page, workspaceLayout: layout };
  });
}

/** 紧挨指定页右侧摆放（横向模式 / 复制 / 插入） */
export function workspaceLayoutAfterPage(page: Page, pages: Page[]): PageWorkspaceLayout {
  let maxZ = 0;
  for (const p of pages) {
    maxZ = Math.max(maxZ, p.workspaceLayout?.zIndex ?? 0);
  }
  return {
    x: page.workspaceLayout.x + page.size.width + PAGE_WORKSPACE_GAP,
    y: page.workspaceLayout.y,
    zIndex: maxZ + 1,
  };
}

/**
 * 从锚点页的下一页起，按布局模式链式重排坐标（插入/复制后避免与后续页重叠）。
 * @param anchorIndex 锚点页在 pages 中的下标；重排范围为 [anchorIndex + 1, …)
 */
export function relayoutPagesFromIndex(
  pages: Page[],
  anchorIndex: number,
  mode: Exclude<WorkspacePageLayoutMode, 'single'>,
  zoom: number,
): void {
  if (anchorIndex < 0 || anchorIndex >= pages.length - 1) return;

  for (let i = anchorIndex + 1; i < pages.length; i++) {
    const prev = pages[i - 1]!;
    const page = pages[i]!;
    const nextLayout =
      mode === 'vertical'
        ? workspaceLayoutBelowPage(prev, pages, zoom)
        : workspaceLayoutAfterPage(prev, pages);
    const rotation = page.workspaceLayout?.rotation;
    page.workspaceLayout = {
      ...nextLayout,
      zIndex: i,
      ...(rotation !== undefined ? { rotation } : {}),
    };
    page.updatedAt = Date.now();
  }
}

/** 新建页时，紧挨已有页列表右侧摆放（横向默认） */
export function nextPageWorkspaceLayout(pages: Page[]): PageWorkspaceLayout {
  if (pages.length === 0) {
    return { x: 0, y: 0, zIndex: 0 };
  }
  let maxRight = 0;
  let maxZ = 0;
  for (const page of pages) {
    const wl = page.workspaceLayout ?? { x: 0, y: 0, zIndex: 0 };
    maxRight = Math.max(maxRight, wl.x + page.size.width);
    maxZ = Math.max(maxZ, wl.zIndex ?? 0);
  }
  const last = pages[pages.length - 1]!;
  const lastWl = last.workspaceLayout ?? { x: 0, y: 0, zIndex: pages.length - 1 };
  return {
    x: lastWl.x + last.size.width + PAGE_WORKSPACE_GAP,
    y: lastWl.y,
    zIndex: maxZ + 1,
  };
}

/** 为缺省 workspaceLayout 的历史 Page 补全世界坐标（水平平铺） */
export function normalizePagesWorkspaceLayout(pages: Page[]): Page[] {
  let cursorX = 0;
  return pages.map((page, index) => {
    const wl = page.workspaceLayout;
    if (wl != null && Number.isFinite(wl.x) && Number.isFinite(wl.y)) {
      return {
        ...page,
        workspaceLayout: {
          x: wl.x,
          y: wl.y,
          zIndex: wl.zIndex ?? index,
          ...(wl.rotation !== undefined ? { rotation: wl.rotation } : {}),
        },
      };
    }
    const layout: PageWorkspaceLayout = { x: cursorX, y: 0, zIndex: index };
    cursorX += Math.max(1, page.size.width) + PAGE_WORKSPACE_GAP;
    return { ...page, workspaceLayout: layout };
  });
}

/**
 * 应用 workspaceLayout 补丁。Single 模式下若改动 x/y，整叠页面一起平移。
 */
export function applyWorkspaceLayoutPatch(
  pages: Page[],
  pageId: string,
  patch: Partial<PageWorkspaceLayout>,
  mode: WorkspacePageLayoutMode,
): void {
  const movesStack =
    mode === 'single' && (patch.x !== undefined || patch.y !== undefined);

  if (movesStack) {
    const anchor = pages.find((p) => p.id === pageId) ?? pages[0];
    if (!anchor) return;
    const merged = { ...anchor.workspaceLayout, ...patch };
    const now = Date.now();
    for (const p of pages) {
      p.workspaceLayout = {
        ...p.workspaceLayout,
        x: merged.x,
        y: merged.y,
      };
      p.updatedAt = now;
    }
    return;
  }

  const page = pages.find((p) => p.id === pageId);
  if (!page) return;
  page.workspaceLayout = { ...page.workspaceLayout, ...patch };
  page.updatedAt = Date.now();
}

/**
 * 单页模式可见页：无激活页时展示全部（工作区点击不隐藏）；
 * 有激活页时仅展示当前页及之后页（切换时隐藏「上面」的页，为动画预留）。
 */
export function getSingleModeVisiblePageIds(
  pages: Page[],
  currentPageId: string,
  draggingPageId: string | null,
): Set<string> {
  const ids = new Set<string>();
  if (!currentPageId) {
    for (const p of pages) ids.add(p.id);
  } else {
    const idx = pages.findIndex((p) => p.id === currentPageId);
    if (idx < 0) {
      ids.add(currentPageId);
    } else {
      for (let i = idx; i < pages.length; i++) {
        ids.add(pages[i]!.id);
      }
    }
  }
  if (draggingPageId) ids.add(draggingPageId);
  return ids;
}

/**
 * 单页模式用户可见的最上层页面（与 PagesLayer `sortPagesForRender` + `visiblePageIds` 一致）。
 * 工作区拖入页时仅能落入该页，避免叠放坐标相同导致误识别其它页。
 */
export function getSingleModeTopmostVisiblePageId(
  pages: Page[],
  currentPageId: string,
  draggingPageId: string | null,
): string | null {
  const visibleIds = getSingleModeVisiblePageIds(pages, currentPageId, draggingPageId);
  const visible = pages.filter((p) => visibleIds.has(p.id));
  if (visible.length === 0) return null;
  const ordered = sortPagesForRender(visible, currentPageId);
  return ordered[ordered.length - 1]?.id ?? null;
}

/** 渲染顺序：zIndex 升序，当前激活页置于最上层 */
export function sortPagesForRender(pages: Page[], currentPageId: string): Page[] {
  const withKey = pages.map((page, index) => ({
    page,
    z: page.workspaceLayout.zIndex ?? index,
  }));
  withKey.sort((a, b) => a.z - b.z);
  const sorted = withKey.map((x) => x.page);
  const activeIdx = sorted.findIndex((p) => p.id === currentPageId);
  if (activeIdx < 0) return sorted;
  const active = sorted[activeIdx]!;
  return [...sorted.slice(0, activeIdx), ...sorted.slice(activeIdx + 1), active];
}
